import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Grid from "../Grid/grid";
import Title from "../title";
import technologiesData from "./data/technologies-data";

function TechnologiesStack() {
    const [activeCategory, setActiveCategory] = useState<number>(
        technologiesData[0].id
    );
    const navigate = useNavigate();

    const current = technologiesData.find((c) => c.id === activeCategory)!;

    return (
        <section
            id="tech-stack"
            className="w-full min-h-screen h-full relative flex flex-col overflow-hidden p-2 lg:p-16"
        >
            <Grid size={100} direction="top-left" />

            <div className="z-10 flex justify-start items-start mb-12 w-full">
                <Title title="Our Complete Tech Arsenal" />
            </div>

            <div className="z-10 max-w-5xl mb-12">
                <h2 className="md:text-4xl text-xl font-medium md:font-light text-gray-900 leading-tight">
                    A curated stack, engineered for{" "}
                    <span className="text-primary font-medium">
                        performance, scale and delight.
                    </span>
                </h2>
                <p className="mt-4 text-gray-600 md:text-lg max-w-3xl">
                    Explore the frameworks, languages, databases, and platforms we use to
                    bring modern products to life.
                </p>
            </div>

            {/* Category tabs */}
            <div className="z-10 flex flex-wrap gap-3 mb-10">
                {technologiesData.map((cat) => (
                    <motion.button
                        key={cat.id}
                        onClick={() => setActiveCategory(cat.id)}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.97 }}
                        className={`px-5 py-2.5 rounded-full text-sm md:text-base font-medium border transition-all duration-300 ${activeCategory === cat.id
                            ? "bg-primary text-white border-primary shadow-md shadow-primary/20"
                            : "bg-white text-gray-700 border-gray-200 hover:border-primary hover:text-primary"
                            }`}
                    >
                        {cat.title}
                    </motion.button>
                ))}
            </div>

            {/* Category header */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={current.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.35 }}
                    className="z-10"
                >
                    <div className="mb-8">
                        <h3 className="text-2xl md:text-3xl font-semibold text-primary">
                            {current.title}
                        </h3>
                        <p className="mt-2 text-gray-600 md:text-lg max-w-3xl">
                            {current.subtitle}
                        </p>
                    </div>

                    {/* Technologies grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
                        {current.technologies.map((tech, index) => (
                            <motion.div
                                key={tech.name}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35, delay: index * 0.05 }}
                                whileHover={{ y: -6, scale: 1.03 }}
                                onClick={() => tech.path && navigate(tech.path)}
                                className={`group relative bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 p-6 flex flex-col items-center justify-center gap-3 overflow-hidden ${tech.path ? "cursor-pointer" : "cursor-default"
                                    }`}
                            >
                                {/* Colored glow on hover */}
                                <div
                                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                    style={{
                                        background: `radial-gradient(circle at center, rgba(59, 130, 246, 0.1) 0%, transparent 70%)`,
                                    }}
                                />

                                <img
                                    src={tech.logo || "/placeholder.svg"}
                                    alt={tech.name}
                                    className="w-16 h-16 md:w-20 md:h-20 object-contain relative z-10 transition-transform duration-300 group-hover:scale-110"
                                />
                                <p className="relative z-10 text-sm md:text-base font-medium text-gray-800 text-center group-hover:text-primary transition-colors">
                                    {tech.name}
                                </p>

                                {/* Bottom accent bar */}
                                <div
                                    className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500 bg-primary"
                                />
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </AnimatePresence>
        </section>
    );
}

export default TechnologiesStack;
